import { fetchDataFromApi } from './handleApiRequest'
import { SEARCH_RESULTS_PER_PAGE } from '../constants/offerSearch'
import { OfferSearchFilterTypes } from '../typeDefinitions/offerSearch'
import { setSearchUrlParam } from '../UrlUtils'

/**
 * Handles M043 event request
 * @param {string} apiUrl
 * @param {string} authorisationKey
 * @param {string|number} eventId
 * @param {function} success
 * @param {function} error
 */
export const handleOfferSearchEventRequest = (
  apiUrl,
  authorisationKey,
  eventId,
  searchModeId,
  zip,
  city,
  success,
  error
) => {
  // for testing errors
  // const ZD_EVENTS_EVENTS = 'http://localhost:4000/api/other/general-error.json'
  const urlParams = new URLSearchParams()

  const url = apiUrl.replace(/%EVENT_ID%/g, eventId)
  const headers = { 'api-authorisation-key': authorisationKey || '' }
  urlParams.append('search_mode', searchModeId)
  if (zip && city) {
    urlParams.append('address', `${zip} ${city}`)
  }

  fetchDataFromApi({ url, headers, params: urlParams })
    .then((response) => {
      success(response)
    })
    .catch((response) => {
      error(response)
    })
}

/**
 * Handles offer search event request
 * @param {string} apiUrl
 * @param {string} authorisationKey
 * @param {int} clientId
 * @param {int} offset
 * @param {int|null|undefined} searchMode
 * @param {Array<object>} currentFilters
 * @param {int|undefined} radius
 * @param {string|null} locationSearchQuery
 * @param {string|undefined} keywordSearchQuery
 * @param {object|undefined} coordinates
 * @param {function} success
 * @param {function} error
 */
export const handleOfferSearchLocationEventsRequest = (
  apiUrl,
  authorisationKey,
  clientId,
  offset = 0,
  searchMode,
  currentFilters,
  radius,
  locationSearchQuery,
  keywordSearchQuery,
  coordinates,
  success,
  error
) => {
  const params = getFiltersAsRequestParams(currentFilters)
  const headers = { 'api-authorisation-key': authorisationKey }

  params.set('limit', SEARCH_RESULTS_PER_PAGE.toString())
  params.set('hide_filter', 'true')

  params.set('offset', offset)
  if (searchMode !== null && searchMode !== undefined) params.set('search_mode', searchMode)
  if (!!keywordSearchQuery) params.set('filter[fulltext]', keywordSearchQuery)
  if (locationSearchQuery && radius)
    params.set('filter[distance]', JSON.stringify({ radius: parseInt(radius), search_query: locationSearchQuery }))
  if (coordinates.lat && coordinates.lng) {
    params.set('filter[lat]', coordinates.lat)
    params.set('filter[lng]', coordinates.lng)
  }

  const url = apiUrl.replace(/%CLIENT%/g, clientId)

  fetchDataFromApi({ url, params, headers })
    .then((response) => {
      success(response)
    })
    .catch((response) => {
      error(response)
    })
}

/**
 * Handles M043 event request
 * @param {string} apiUrl
 * @param {string} authorisationKey
 * @param {string|number} clientId
 * @param {object|null} clientLocation
 * @param {array} params
 * @param {function} success
 * @param {function} error
 */
export const handleOfferSearchEventsRequest = (
  apiUrl,
  authorisationKey,
  clientId,
  clientLocation,
  params,
  success,
  error
) => {
  // for testing errors
  // const ZD_EVENTS_EVENTS = 'http://localhost:4000/api/other/general-error.json'

  const urlParams = new URLSearchParams()
  for (const param of params) {
    if (typeof param?.value === 'string' && param.value.includes('%LOCATION%') && clientLocation) {
      let completeValue = param.value.replace('%LOCATION%', `${clientLocation.zip} ${clientLocation.city}`)
      urlParams.append(param.name, completeValue)
    } else {
      setSearchUrlParam(param, urlParams)
    }
  }

  let url = apiUrl.replace(/%CLIENT%/g, clientId)
  const headers = { 'api-authorisation-key': authorisationKey || '' }

  fetchDataFromApi({ url, headers, params: urlParams })
    .then((response) => {
      success(response)
    })
    .catch((response) => {
      error(response)
    })
}

/**
 * Handles offer search event request
 * @param {string} apiUrl
 * @param {string} authorisationKey
 * @param {int} clientId
 * @param {int} offset
 * @param {int|null|undefined} searchMode
 * @param {Array<object>} currentFilters
 * @param {int|undefined} radius
 * @param {string|null} locationSearchQuery
 * @param {string|undefined} keywordSearchQuery
 * @returns {Promise} with result data
 */
export const handleOfferSearchEventFilterRequest = (
  apiUrl,
  authorisationKey,
  clientId,
  offset = 0,
  searchMode,
  currentFilters,
  radius,
  locationSearchQuery,
  keywordSearchQuery
) => {
  const params = getFiltersAsRequestParams(currentFilters)
  const headers = { 'api-authorisation-key': authorisationKey }

  params.set('limit', SEARCH_RESULTS_PER_PAGE.toString())
  params.set('hide_filter', 'true')

  params.set('offset', offset)
  if (searchMode !== null && searchMode !== undefined) params.set('search_mode', searchMode)
  if (!!keywordSearchQuery) params.set('filter[fulltext]', keywordSearchQuery)
  if (locationSearchQuery && radius)
    params.set('filter[distance]', JSON.stringify({ radius: parseInt(radius), search_query: locationSearchQuery }))

  const url = apiUrl.replace(/%CLIENT%/g, clientId)

  return fetchDataFromApi({ url, params, headers })
}

/**
 * Handles offer search event request
 * @param {string} apiUrl
 * @param {string} authorisationKey
 * @param {int} clientId
 * @param {int} offset
 * @param {int|null|undefined} searchMode
 * @param {Array<object>} currentFilters
 * @param {int|undefined} radius
 * @param {string|null} locationSearchQuery
 * @param {string|undefined} keywordSearchQuery
 * @returns {Promise} with result data
 */
export const handleOfferSearchLocationsFilterRequest = (
  apiUrl,
  authorisationKey,
  clientId,
  offset = 0,
  searchMode,
  currentFilters,
  radius,
  locationSearchQuery,
  keywordSearchQuery
) => {
  const params = getFiltersAsRequestParams(currentFilters)
  const headers = { 'api-authorisation-key': authorisationKey }

  params.set('hide_filter', 'true')

  params.set('limit', '2000')
  params.set('offset', offset)
  if (searchMode !== null && searchMode !== undefined) params.set('search_mode', searchMode)
  if (!!keywordSearchQuery) {
    params.set('filter[fulltext]', keywordSearchQuery)
  }
  if (locationSearchQuery && radius)
    params.set('filter[distance]', JSON.stringify({ radius: parseInt(radius), search_query: locationSearchQuery }))

  const url = apiUrl.replace(/%CLIENT%/g, clientId)

  return fetchDataFromApi({ url, params, headers })
}

/**
 * Returns filters state prepared as request params ready to be sent
 * @param {Array<object>} currentFilters Current filters object
 * @returns {URLSearchParams} filters state as request params
 */
const getFiltersAsRequestParams = (currentFilters) => {
  let searchParams = new URLSearchParams()

  for (const filter of currentFilters) {
    switch (filter.type) {
      case OfferSearchFilterTypes.TOPIC_CATEGORIES:
      case OfferSearchFilterTypes.TOPICS:
      case OfferSearchFilterTypes.AUDIENCES:
      case OfferSearchFilterTypes.STATE_NOT:
        if (!!filter.state && !!filter.state?.length) {
          searchParams.append(`filter[${filter.type}]`, JSON.stringify(filter.state))
        }
        break

      case OfferSearchFilterTypes.EVENT_TYPES:
        if (!!filter.state && !!filter.state.training_types?.length) {
          searchParams.append(`filter[training_types]`, JSON.stringify(filter.state.training_types))
        }
        if (!!filter.state && !!filter.state.day_span?.length) {
          searchParams.append(`filter[day_span]`, JSON.stringify(filter.state.day_span))
        }
        break

      case OfferSearchFilterTypes.PERIOD:
        if (!!filter.state[0]) {
          searchParams.append(`filter[start_date_from]`, JSON.stringify(filter.state[0]))
        } else {
          searchParams.append(`filter[start_date_from]`, JSON.stringify(new Date()))
        }
        if (!!filter.state[1]) {
          searchParams.append(`filter[end_date_to]`, JSON.stringify(filter.state[1]))
        }
        break

      case OfferSearchFilterTypes.SORTING:
        if (!!filter.state) {
          searchParams.append(`${filter.type}`, filter.state)
        }
        break

      case OfferSearchFilterTypes.ONLINE_TYPE:
        if (!!filter.state) {
          if (filter.state === 'allCourses' || filter.state === '') {
            searchParams.delete(`filter[${filter.type}]`)
          } else {
            const filterState = filter.state === 'offline' ? [filter.state, 'hybrid'] : [filter.state]
            searchParams.append(`filter[${filter.type}]`, JSON.stringify(filterState))
          }
        }
        break

      default:
        break
    }
  }

  return searchParams
}
